import { useSaveMentionBoostSetup } from '@/hooks/actions/use-save-mention-boost-setup';
import { useShopifyIntegrationQuery } from '@/hooks/queries/use-integrations-query';
import { useMentionBoostSetupQuery } from '@/hooks/queries/use-mention-boost-setup-query';
import { type ShopifyProduct } from '@/hooks/queries/use-shopify-products-query';
import { useAuth } from '@/contexts/auth-context';
import type { ShopifyMetadata } from '@/types/integration';
import {
  ALLOWED_SHIPPING_COUNTRIES_UPPER_SET,
  MentionBoostDestinationType,
  MentionBoostPlacementMode,
  MentionBoostSetupAudienceMode,
  buildDefaultUtmParameters,
  planUtmApplication,
  toMetaBoostBudgetUnits,
  type MentionBoostDto,
  type MentionBoostEligibilityResponse,
  type MentionBoostReadinessResponse,
  type MentionBoostSetupDto,
  type MentionBoostSetupProductDto,
  type MentionBoostTargetAgeRangeDto,
  type MentionBoostTargetLocationDto,
  type SaveMentionBoostSetupRequest,
} from '@fluenceur/types';
import { createListCollection } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildProductUrl,
  buildTargetLocation,
  cleanUtmParameters,
  isValidHttpUrl,
  normalizeUrlBase,
  productFromShopifyProduct,
  toUtmRows,
  type UtmRow,
} from './utils';

// TARGET_AGE_MIN is 18 — a deliberate Fluenceur policy floor.
// Meta's actual minimum is 13; do not lower this value.
const TARGET_AGE_MIN = 18;
const TARGET_AGE_MAX = 65;
const ADVANTAGE_PLUS_AGE_MIN_MAX = 25;
export const BOOST_SETUP_FIELD_HEIGHT = '40px';

export const DEFAULT_BUDGET_AMOUNT_IN_CENTS = 5000;
const DEFAULT_BUDGET_CURRENCY_CODE = 'USD';
export const DEFAULT_DURATION_DAYS = 7;
export const DEFAULT_TARGET_AGE_RANGE: MentionBoostTargetAgeRangeDto = { min: 18, max: 65 };

export const budgetPresets = [2500, 5000, 10000, 20000];
export const durationPresets = [3, 7, 14];

const ageOptions = Array.from(
  { length: TARGET_AGE_MAX - TARGET_AGE_MIN + 1 },
  (_, index) => TARGET_AGE_MIN + index
);
const advantagePlusAgeOptions = ageOptions.filter((age) => age <= ADVANTAGE_PLUS_AGE_MIN_MAX);

// Stable serialization of a save payload for dirty-tracking. Locations collapse
// to their sorted `key` set (a location is fully identified by its key), and the
// fields are emitted in a fixed order so object/array key order never causes a
// false dirty result.
const serializeDraft = (payload: SaveMentionBoostSetupRequest): string =>
  JSON.stringify({
    destinationType: payload.destinationType,
    shopifyProductId: payload.product?.shopifyProductId ?? null,
    targetUrl: payload.targetUrl ?? null,
    budgetAmountInCents: payload.budgetAmountInCents,
    durationDays: payload.durationDays,
    audienceMode: payload.audienceMode,
    locationKeys: payload.targetLocations.map((location) => location.key).sort(),
    ageMin: payload.targetAgeRange.min,
    ageMax: payload.targetAgeRange.max,
    // null (pristine convention) vs [] (disabled) vs the ordered pair list are
    // all meaningful, so serialize the value as-is (order carried).
    utmParameters: payload.utmParameters ?? null,
    placementMode: payload.placementMode ?? MentionBoostPlacementMode.NEW_CAMPAIGN,
    placementCampaignId: payload.placementCampaignId ?? null,
  });

export interface MentionBoostSetupFormState {
  // data loading
  isLoading: boolean;
  isError: boolean;
  // True while a background draft save is in flight.
  isSaving: boolean;
  // server-side boost state
  setup: MentionBoostSetupDto | null;
  launchedBoost: MentionBoostDto | null;
  eligibility: MentionBoostEligibilityResponse | null;
  // Ad-account readiness driving the launch dialog's account picker. Null when
  // the mention is not in a launchable state.
  readiness: MentionBoostReadinessResponse | null;
  // shopify
  isShopifyConnected: boolean;
  // destination
  destinationType: MentionBoostDestinationType;
  setDestinationType: (type: MentionBoostDestinationType) => void;
  // product (PRODUCT_PAGE only)
  selectedProduct: MentionBoostSetupProductDto | null;
  selectedProductIds: string[];
  handleProductToggle: (product: ShopifyProduct) => void;
  selectedProductUrl: string | null;
  // url (PRODUCT_PAGE optional override, WEBSITE required)
  customTargetUrlEnabled: boolean;
  targetUrl: string;
  setTargetUrl: (url: string) => void;
  handleCustomTargetUrlChange: (event: { checked: boolean | 'indeterminate' }) => void;
  targetUrlIsValid: boolean;
  targetUrlIsInvalid: boolean;
  targetUrlPreview: string | null;
  // website destination URL (direct input, no custom-override toggle)
  websiteUrl: string;
  setWebsiteUrl: (url: string) => void;
  websiteUrlIsValid: boolean;
  websiteUrlIsInvalid: boolean;
  // UTM tracking on the destination link
  // The section shows only when a destination URL is resolved.
  showUtmSection: boolean;
  // True while the brand has not touched UTM (drives the "Convention Fluenceur"
  // badge and the dynamic-default recompute).
  isUtmConventionPristine: boolean;
  // The editable row list: the default convention (when pristine) or the
  // materialised list. Each row carries a stable client id for React keys.
  utmDisplayParameters: UtmRow[];
  // Convention keys skipped because the URL already carries them (value taken
  // from the URL, rendered locked).
  utmLockedKeys: Array<{ key: string; value: string }>;
  // The resolved destination URL, and the final preview with the applied UTM.
  utmDestinationUrl: string | null;
  utmPreviewUrl: string | null;
  // The query string actually appended (no leading `?`), used to highlight the
  // added part of the preview without re-deriving it from string prefixes.
  utmUrlTags: string | null;
  // Number of pairs actually applied (used in the launch recap).
  utmAppliedCount: number;
  onUtmValueChange: (index: number, value: string) => void;
  onUtmKeyChange: (index: number, key: string) => void;
  onUtmAddParameter: () => void;
  onUtmRemoveParameter: (index: number) => void;
  // Clear every pair at once (tracking off) and restore the convention.
  onUtmDisableTracking: () => void;
  onUtmEnableTracking: () => void;
  // placement (where the ad set is created on Meta)
  placementMode: MentionBoostPlacementMode;
  placementCampaignId: string | null;
  placementCampaignName: string | null;
  setPlacement: (
    mode: MentionBoostPlacementMode,
    campaign: { campaignId: string; campaignName: string } | null
  ) => void;
  // budget / duration
  budgetAmountInCents: number;
  budgetCurrencyCode: string;
  budgetCurrencyDisplay: string;
  budgetWholeUnitInvalid: boolean;
  durationDays: number;
  handleBudgetChange: (details: { valueAsNumber: number }) => void;
  handleDurationChange: (value: string[]) => void;
  durationCollection: ReturnType<typeof createListCollection<{ label: string; value: string }>>;
  // audience
  audienceMode: MentionBoostSetupAudienceMode;
  advantagePlusEnabled: boolean;
  handleAdvantagePlusChange: (event: { checked: boolean | 'indeterminate' }) => void;
  targetLocations: MentionBoostTargetLocationDto[];
  setTargetLocations: (locations: MentionBoostTargetLocationDto[]) => void;
  targetAgeRange: MentionBoostTargetAgeRangeDto;
  ageCollection: ReturnType<typeof createListCollection<{ label: string; value: string }>>;
  advantagePlusAgeCollection: ReturnType<
    typeof createListCollection<{ label: string; value: string }>
  >;
  advantagePlusMinimumAgeInvalid: boolean;
  handleTargetAgeMinChange: (value: string[]) => void;
  handleTargetAgeMaxChange: (value: string[]) => void;
  // derived
  canSave: boolean;
  // True when the current draft diverges from the last persisted/default state.
  isDirty: boolean;
  // Persists the current draft. Resolves once the save lands so the launch flow
  // can await it; rejects (and toasts) on failure. Does not close the panel.
  saveDraft: () => Promise<void>;
}

interface UseMentionBoostSetupFormOptions {
  isOpen: boolean;
  mentionId: string;
  // The ad account the marketer picked, so the readiness in the response is
  // resolved against it. Null leaves the server on the brand's saved default.
  preferredAdAccountId?: string | null;
  // Creator handle for the default UTM convention (utm_content and, for a
  // website destination, utm_campaign). Empty when unknown.
  creatorUsername?: string;
  // Kept for call-site compatibility (the panel still passes it). The form no
  // longer closes the panel on save: saves are silent and the panel owns its own
  // lifecycle.
  onClose: () => void;
}

export function useMentionBoostSetupForm({
  isOpen,
  mentionId,
  preferredAdAccountId = null,
  creatorUsername = '',
}: UseMentionBoostSetupFormOptions): MentionBoostSetupFormState {
  const { i18n, t } = useTranslation();
  const { user } = useAuth();
  const { data, isError, isLoading } = useMentionBoostSetupQuery({
    mentionId,
    enabled: isOpen,
    preferredAdAccountId,
  });
  const { data: shopifyIntegration } = useShopifyIntegrationQuery();
  const { isPending: isSaving, mutateAsync: saveSetupAsync } = useSaveMentionBoostSetup();

  const brandCurrencyCode =
    user?.currentBrand?.currency?.toUpperCase() ?? DEFAULT_BUDGET_CURRENCY_CODE;
  const brandCountryCode = user?.currentBrand?.companyDetails?.country?.toUpperCase();
  const shopifyMetadata = shopifyIntegration?.metadata as ShopifyMetadata | undefined;

  const isShopifyConnected = Boolean(shopifyIntegration?.metadata);

  // ---- form state ----
  const [destinationType, setDestinationType] = useState<MentionBoostDestinationType>(
    isShopifyConnected
      ? MentionBoostDestinationType.PRODUCT_PAGE
      : MentionBoostDestinationType.WEBSITE
  );
  const [selectedProduct, setSelectedProduct] = useState<MentionBoostSetupProductDto | null>(null);
  const [budgetAmountInCents, setBudgetAmountInCents] = useState(DEFAULT_BUDGET_AMOUNT_IN_CENTS);
  const [budgetCurrencyCode, setBudgetCurrencyCode] = useState(brandCurrencyCode);
  const [durationDays, setDurationDays] = useState(DEFAULT_DURATION_DAYS);
  const [audienceMode, setAudienceMode] = useState(MentionBoostSetupAudienceMode.ADVANTAGE_PLUS);
  const [targetLocations, setTargetLocations] = useState<MentionBoostTargetLocationDto[]>([]);
  const [targetAgeRange, setTargetAgeRange] =
    useState<MentionBoostTargetAgeRangeDto>(DEFAULT_TARGET_AGE_RANGE);
  // PRODUCT_PAGE: optional custom URL override
  const [customTargetUrlEnabled, setCustomTargetUrlEnabled] = useState(false);
  const [targetUrl, setTargetUrl] = useState('');
  // WEBSITE: direct required URL
  const [websiteUrl, setWebsiteUrl] = useState('');
  // UTM: null = pristine convention (recompute default dynamically); [] = disabled;
  // a list = the brand's materialised edits.
  const [utmParameters, setUtmParameters] = useState<UtmRow[] | null>(null);
  // Placement: the default keeps the historical parcours (Fluenceur creates and
  // owns a new campaign); the campaign fields are set only in the opt-in mode.
  const [placementMode, setPlacementMode] = useState<MentionBoostPlacementMode>(
    MentionBoostPlacementMode.NEW_CAMPAIGN
  );
  const [placementCampaignId, setPlacementCampaignId] = useState<string | null>(null);
  const [placementCampaignName, setPlacementCampaignName] = useState<string | null>(null);

  // Snapshot of the last persisted (server) or freshly-reset draft, in the exact
  // serialized shape `currentDraft` produces. `isDirty` compares against it, so
  // auto-save and the launch flow only persist real edits. Set in the hydrate
  // effect (both the server-setup and reset branches) so the baseline always
  // tracks the latest known-good state.
  const baselineRef = useRef<string>('');

  // Latest-value mirror of `isDirty`, read by the hydrate effect to decide whether
  // a server-setup change may overwrite local fields. Assigned during render (it
  // is computed below), so the effect always sees the current value without
  // needing `isDirty` in its dependency array.
  const isDirtyRef = useRef(false);

  // Whether a baseline has been established for the current open session. Until it
  // is, `isDirty` reads as spuriously true (baseline starts ''), so the dirty
  // guard must not yet apply; the first hydration always runs. Re-armed on close.
  const hasHydratedRef = useRef(false);

  // ---- default target location ----
  // Only seed the brand's country when it is a supported targeting country;
  // a brand registered outside the shipping zone starts with no default.
  const defaultTargetLocations = useMemo(
    () =>
      brandCountryCode &&
      (ALLOWED_SHIPPING_COUNTRIES_UPPER_SET as ReadonlySet<string>).has(brandCountryCode)
        ? [buildTargetLocation(brandCountryCode, i18n.language)]
        : [],
    [brandCountryCode, i18n.language]
  );

  // ---- reset / hydrate ----
  const resetDraft = useCallback(() => {
    const defaultDestination = isShopifyConnected
      ? MentionBoostDestinationType.PRODUCT_PAGE
      : MentionBoostDestinationType.WEBSITE;
    setDestinationType(defaultDestination);
    setSelectedProduct(null);
    setBudgetAmountInCents(DEFAULT_BUDGET_AMOUNT_IN_CENTS);
    setBudgetCurrencyCode(brandCurrencyCode);
    setDurationDays(DEFAULT_DURATION_DAYS);
    setAudienceMode(MentionBoostSetupAudienceMode.ADVANTAGE_PLUS);
    setTargetLocations(defaultTargetLocations);
    setTargetAgeRange(DEFAULT_TARGET_AGE_RANGE);
    setCustomTargetUrlEnabled(false);
    setTargetUrl('');
    setWebsiteUrl('');
    setUtmParameters(null);
    setPlacementMode(MentionBoostPlacementMode.NEW_CAMPAIGN);
    setPlacementCampaignId(null);
    setPlacementCampaignName(null);
  }, [brandCurrencyCode, defaultTargetLocations, isShopifyConnected]);

  useEffect(() => {
    // The freshly-reset default state becomes the dirty baseline, so an untouched
    // form never auto-saves and never opens the launch confirm pretending it is
    // dirty.
    const applyDefaultBaseline = () => {
      resetDraft();
      const defaultDestination = isShopifyConnected
        ? MentionBoostDestinationType.PRODUCT_PAGE
        : MentionBoostDestinationType.WEBSITE;
      baselineRef.current = serializeDraft({
        destinationType: defaultDestination,
        product: null,
        targetUrl: null,
        budgetAmountInCents: DEFAULT_BUDGET_AMOUNT_IN_CENTS,
        durationDays: DEFAULT_DURATION_DAYS,
        audienceMode: MentionBoostSetupAudienceMode.ADVANTAGE_PLUS,
        targetLocations: defaultTargetLocations,
        targetAgeRange: DEFAULT_TARGET_AGE_RANGE,
        utmParameters: null,
        placementMode: MentionBoostPlacementMode.NEW_CAMPAIGN,
        placementCampaignId: null,
      });
    };

    // Closing always resets and re-arms hydration for the next open, even if the
    // draft was left dirty mid-edit, so a reopen never restores a stale draft.
    if (!isOpen) {
      applyDefaultBaseline();
      hasHydratedRef.current = false;
      return;
    }

    // Once a baseline exists for this session, a later server-setup change — the
    // save mutation writing the just-saved setup back into the cache, or a
    // background refetch — must not overwrite unsaved local edits. Skipping while
    // the draft is dirty or a save is in flight keeps an edit made during that
    // window from being clobbered by the older persisted payload.
    if (hasHydratedRef.current && (isDirtyRef.current || isSaving)) return;

    if (!data?.setup) {
      applyDefaultBaseline();
      hasHydratedRef.current = true;
      return;
    }

    const setup = data.setup;
    setDestinationType(setup.destinationType);
    setSelectedProduct(setup.product);
    setBudgetAmountInCents(setup.budgetAmountInCents);
    // budgetCurrencyCode comes from server; display it but do not send it back
    setBudgetCurrencyCode(setup.budgetCurrencyCode);
    setDurationDays(setup.durationDays);
    setAudienceMode(setup.audienceMode);
    setTargetLocations(setup.targetLocations);
    setTargetAgeRange(setup.targetAgeRange);
    setUtmParameters(setup.utmParameters ? toUtmRows(setup.utmParameters) : null);
    setPlacementMode(setup.placementMode);
    setPlacementCampaignId(setup.placementCampaignId);
    setPlacementCampaignName(setup.placementCampaignName);

    if (setup.destinationType === MentionBoostDestinationType.PRODUCT_PAGE) {
      setCustomTargetUrlEnabled(!!setup.targetUrl);
      setTargetUrl(setup.targetUrl ?? '');
      setWebsiteUrl('');
    } else if (setup.destinationType === MentionBoostDestinationType.WEBSITE) {
      setWebsiteUrl(setup.targetUrl ?? '');
      setCustomTargetUrlEnabled(false);
      setTargetUrl('');
    } else {
      setCustomTargetUrlEnabled(false);
      setTargetUrl('');
      setWebsiteUrl('');
    }

    // The persisted setup is the dirty baseline; subsequent edits diverge from it.
    baselineRef.current = serializeDraft({
      destinationType: setup.destinationType,
      product:
        setup.destinationType === MentionBoostDestinationType.PRODUCT_PAGE && setup.product
          ? { shopifyProductId: setup.product.shopifyProductId }
          : null,
      targetUrl: setup.targetUrl ?? null,
      budgetAmountInCents: setup.budgetAmountInCents,
      durationDays: setup.durationDays,
      audienceMode: setup.audienceMode,
      targetLocations: setup.targetLocations,
      targetAgeRange: setup.targetAgeRange,
      utmParameters: cleanUtmParameters(setup.utmParameters ?? null),
      placementMode: setup.placementMode,
      placementCampaignId: setup.placementCampaignId,
    });
    hasHydratedRef.current = true;
  }, [
    data?.setup,
    defaultTargetLocations,
    isOpen,
    isSaving,
    isShopifyConnected,
    mentionId,
    resetDraft,
  ]);

  // ---- collections ----
  const durationCollection = useMemo(
    () =>
      createListCollection({
        items: durationPresets.map((value) => ({
          label: t('mentions.boost.setup.durationPreset', { count: value }),
          value: String(value),
        })),
      }),
    [t]
  );

  const ageCollection = useMemo(
    () =>
      createListCollection({
        items: ageOptions.map((value) => ({
          label:
            value === TARGET_AGE_MAX
              ? t('mentions.boost.setup.agePresetMax', { count: value })
              : String(value),
          value: String(value),
        })),
      }),
    [t]
  );

  const advantagePlusAgeCollection = useMemo(
    () =>
      createListCollection({
        items: advantagePlusAgeOptions.map((value) => ({
          label: String(value),
          value: String(value),
        })),
      }),
    []
  );

  // ---- derived display ----
  const budgetCurrencyDisplay = useMemo(() => {
    try {
      return (
        new Intl.NumberFormat(t('common.locale'), {
          style: 'currency',
          currency: budgetCurrencyCode,
          currencyDisplay: 'narrowSymbol',
        })
          .formatToParts(0)
          .find((part) => part.type === 'currency')?.value ?? budgetCurrencyCode
      );
    } catch {
      return budgetCurrencyCode;
    }
  }, [budgetCurrencyCode, t]);

  const productUrlBase = useMemo(
    () => normalizeUrlBase(shopifyMetadata?.primaryDomainUrl ?? shopifyMetadata?.shopDomain),

    [shopifyMetadata?.primaryDomainUrl, shopifyMetadata?.shopDomain]
  );

  const selectedProductUrl = useMemo(
    () => buildProductUrl(productUrlBase, selectedProduct?.handle),
    [productUrlBase, selectedProduct?.handle]
  );

  const trimmedTargetUrl = targetUrl.trim();
  const trimmedWebsiteUrl = websiteUrl.trim();

  const targetUrlIsValid = !customTargetUrlEnabled || isValidHttpUrl(trimmedTargetUrl);
  const targetUrlIsInvalid =
    customTargetUrlEnabled && trimmedTargetUrl.length > 0 && !targetUrlIsValid;

  const websiteUrlIsValid = isValidHttpUrl(trimmedWebsiteUrl);
  const websiteUrlIsInvalid = trimmedWebsiteUrl.length > 0 && !websiteUrlIsValid;

  const targetUrlPreview = customTargetUrlEnabled
    ? trimmedTargetUrl.length > 0 && isValidHttpUrl(trimmedTargetUrl)
      ? trimmedTargetUrl
      : null
    : selectedProductUrl;

  // ---- UTM ----
  // The destination URL the convention decorates: the resolved product page (or
  // its custom override) for a product destination, or the website URL.
  const utmDestinationUrl =
    destinationType === MentionBoostDestinationType.WEBSITE
      ? websiteUrlIsValid && trimmedWebsiteUrl.length > 0
        ? trimmedWebsiteUrl
        : null
      : targetUrlPreview;

  const showUtmSection = utmDestinationUrl !== null;
  const isUtmConventionPristine = utmParameters === null;

  // Default convention, recomputed as the product/creator change while pristine.
  // The product feeds utm_campaign only for a PRODUCT_PAGE destination: switching
  // to WEBSITE recomputes utm_campaign from the creator slug, never a stale
  // product slug (selectedProduct is not reset on destination change).
  const usesProductForDefault = destinationType === MentionBoostDestinationType.PRODUCT_PAGE;
  const utmDefaultPairs = useMemo(
    () =>
      buildDefaultUtmParameters({
        productHandle: usesProductForDefault ? (selectedProduct?.handle ?? null) : null,
        productTitle: usesProductForDefault ? (selectedProduct?.title ?? null) : null,
        creatorUsername,
      }),
    [creatorUsername, selectedProduct?.handle, selectedProduct?.title, usesProductForDefault]
  );

  const utmDefaultParameters = useMemo(() => toUtmRows(utmDefaultPairs), [utmDefaultPairs]);

  // Once the list is materialised the dynamic default no longer drives it, so a
  // later product change would leave a stale convention value behind (e.g. an
  // utm_content still naming the previous product). Re-sync only the convention
  // rows the brand never edited: a row whose value still equals the default it
  // was materialised from follows the new default; anything typed is untouched.
  const previousDefaultPairsRef = useRef(utmDefaultPairs);
  useEffect(() => {
    const previousPairs = previousDefaultPairsRef.current;
    previousDefaultPairsRef.current = utmDefaultPairs;
    if (previousPairs === utmDefaultPairs) return;

    const previousByKey = new Map(previousPairs.map((pair) => [pair.key, pair.value]));
    const nextByKey = new Map(utmDefaultPairs.map((pair) => [pair.key, pair.value]));

    setUtmParameters((current) => {
      if (current === null || current.length === 0) return current;

      let changed = false;
      const next = current.map((row) => {
        const nextValue = nextByKey.get(row.key);
        if (nextValue === undefined || nextValue === row.value) return row;
        if (previousByKey.get(row.key) !== row.value) return row; // brand-edited
        changed = true;
        return { ...row, value: nextValue };
      });

      return changed ? next : current;
    });
  }, [utmDefaultPairs]);

  const utmDisplayParameters = utmParameters ?? utmDefaultParameters;

  const utmPlan = useMemo(
    () => (utmDestinationUrl ? planUtmApplication(utmDestinationUrl, utmDisplayParameters) : null),
    [utmDestinationUrl, utmDisplayParameters]
  );

  const utmLockedKeys = utmPlan?.lockedKeys ?? [];
  const utmPreviewUrl = utmPlan?.previewUrl ?? null;
  const utmUrlTags = utmPlan?.urlTags ?? null;
  const utmAppliedCount = utmPlan?.appliedParams.length ?? 0;

  const selectedProductIds = useMemo(
    () => (selectedProduct ? [selectedProduct.shopifyProductId] : []),
    [selectedProduct]
  );

  const advantagePlusEnabled = audienceMode === MentionBoostSetupAudienceMode.ADVANTAGE_PLUS;

  // ---- canSave ----
  const advantagePlusMinimumAgeInvalid =
    audienceMode === MentionBoostSetupAudienceMode.ADVANTAGE_PLUS &&
    targetAgeRange.min > ADVANTAGE_PLUS_AGE_MIN_MAX;
  const effectiveTargetAgeRange = useMemo(
    () =>
      audienceMode === MentionBoostSetupAudienceMode.ADVANTAGE_PLUS
        ? { min: targetAgeRange.min, max: TARGET_AGE_MAX }
        : targetAgeRange,
    [audienceMode, targetAgeRange]
  );
  const audienceValid =
    targetLocations.length > 0 &&
    effectiveTargetAgeRange.min >= TARGET_AGE_MIN &&
    effectiveTargetAgeRange.max <= TARGET_AGE_MAX &&
    effectiveTargetAgeRange.min <= effectiveTargetAgeRange.max &&
    !advantagePlusMinimumAgeInvalid;

  const budgetWholeUnitInvalid =
    budgetAmountInCents >= 100 &&
    toMetaBoostBudgetUnits(budgetAmountInCents, budgetCurrencyCode) === null;
  const budgetValid = budgetAmountInCents >= 100 && !budgetWholeUnitInvalid && durationDays >= 1;

  const destinationValid =
    destinationType === MentionBoostDestinationType.PRODUCT_PAGE
      ? !!selectedProduct &&
        targetUrlIsValid &&
        (!customTargetUrlEnabled || trimmedTargetUrl.length > 0)
      : websiteUrlIsValid && trimmedWebsiteUrl.length > 0; // WEBSITE

  // An explicit existing-campaign choice is a different launch contract. Keep
  // that intent intact while the marketer selects a campaign; falling back to a
  // new Fluenceur campaign here could create spend in a place they never chose.
  const placementComplete =
    placementMode !== MentionBoostPlacementMode.EXISTING_CAMPAIGN ||
    (!!placementCampaignId && !!placementCampaignName);

  const canSave = !isError && audienceValid && budgetValid && destinationValid && placementComplete;

  // ---- handlers ----
  const handleProductToggle = useCallback(
    (product: ShopifyProduct) => {
      if (selectedProduct?.shopifyProductId === product.id) {
        setSelectedProduct(null);
        return;
      }
      setSelectedProduct(productFromShopifyProduct(product));
    },
    [selectedProduct?.shopifyProductId]
  );

  const handleBudgetChange = useCallback((details: { valueAsNumber: number }) => {
    const amount = details.valueAsNumber;
    setBudgetAmountInCents(Number.isFinite(amount) ? Math.max(0, Math.round(amount * 100)) : 0);
  }, []);

  const handleDurationChange = useCallback((value: string[]) => {
    const rawValue = value[0];
    if (!rawValue) return;
    const duration = Number(rawValue);
    if (!Number.isFinite(duration)) return;
    setDurationDays(Math.max(1, Math.round(duration)));
  }, []);

  const handleAdvantagePlusChange = useCallback((event: { checked: boolean | 'indeterminate' }) => {
    setAudienceMode(
      event.checked === true
        ? MentionBoostSetupAudienceMode.ADVANTAGE_PLUS
        : MentionBoostSetupAudienceMode.MANUAL
    );
  }, []);

  const handleTargetAgeMinChange = useCallback((value: string[]) => {
    const rawValue = value[0];
    if (!rawValue) return;
    const nextMin = Number(rawValue);
    if (!Number.isFinite(nextMin)) return;
    setTargetAgeRange((current) => ({ min: nextMin, max: Math.max(current.max, nextMin) }));
  }, []);

  const handleTargetAgeMaxChange = useCallback((value: string[]) => {
    const rawValue = value[0];
    if (!rawValue) return;
    const nextMax = Number(rawValue);
    if (!Number.isFinite(nextMax)) return;
    setTargetAgeRange((current) => ({ min: Math.min(current.min, nextMax), max: nextMax }));
  }, []);

  const handleCustomTargetUrlChange = useCallback(
    (event: { checked: boolean | 'indeterminate' }) => {
      const checked = event.checked === true;
      setCustomTargetUrlEnabled(checked);
      if (!checked) setTargetUrl('');
    },
    []
  );

  // All UTM edits materialise the current effective list first (the default
  // convention while still pristine), so the persisted value diverges from the
  // dynamic default exactly at the first edit.
  const onUtmValueChange = useCallback(
    (index: number, value: string) => {
      setUtmParameters((current) => {
        const base = current ?? utmDefaultParameters;
        return base.map((pair, i) => (i === index ? { ...pair, value } : pair));
      });
    },
    [utmDefaultParameters]
  );

  const onUtmKeyChange = useCallback(
    (index: number, key: string) => {
      setUtmParameters((current) => {
        const base = current ?? utmDefaultParameters;
        return base.map((pair, i) => (i === index ? { ...pair, key } : pair));
      });
    },
    [utmDefaultParameters]
  );

  const onUtmAddParameter = useCallback(() => {
    setUtmParameters((current) => [
      ...(current ?? utmDefaultParameters),
      ...toUtmRows([{ key: '', value: '' }]),
    ]);
  }, [utmDefaultParameters]);

  const onUtmRemoveParameter = useCallback(
    (index: number) => {
      setUtmParameters((current) =>
        (current ?? utmDefaultParameters).filter((_, i) => i !== index)
      );
    },
    [utmDefaultParameters]
  );

  // Turn tracking off in one action: an empty list is the persisted "no UTM"
  // state, distinct from null (pristine convention). Reversible — re-enabling
  // returns to the pristine convention rather than a frozen copy of it, so the
  // restored default still follows the product and the creator.
  // Choosing a placement always sets the three persisted fields together: an
  // existing campaign carries its id and its display name, and going back to a
  // new campaign clears both so no stale campaign travels with the launch.
  const setPlacement = useCallback(
    (
      mode: MentionBoostPlacementMode,
      campaign: { campaignId: string; campaignName: string } | null
    ) => {
      setPlacementMode(mode);
      setPlacementCampaignId(
        mode === MentionBoostPlacementMode.EXISTING_CAMPAIGN ? (campaign?.campaignId ?? null) : null
      );
      setPlacementCampaignName(
        mode === MentionBoostPlacementMode.EXISTING_CAMPAIGN
          ? (campaign?.campaignName ?? null)
          : null
      );
    },
    []
  );

  const onUtmDisableTracking = useCallback(() => setUtmParameters([]), []);
  const onUtmEnableTracking = useCallback(() => setUtmParameters(null), []);

  // Single source of truth for the save payload, shared by dirty-tracking and the
  // save mutation, so the two never drift. PRODUCT_PAGE carries the product and an
  // optional custom URL override; WEBSITE carries the required URL.
  // Preserve an incomplete existing-campaign choice in the local payload. It is
  // deliberately unsaveable (see `placementComplete`), rather than rewritten as
  // a new campaign before the marketer can finish the selection.
  const placementPayload = useMemo(
    () =>
      placementMode === MentionBoostPlacementMode.EXISTING_CAMPAIGN
        ? { placementMode, placementCampaignId, placementCampaignName }
        : { placementMode: MentionBoostPlacementMode.NEW_CAMPAIGN },
    [placementCampaignId, placementCampaignName, placementMode]
  );

  const buildPayload = useCallback((): SaveMentionBoostSetupRequest => {
    if (destinationType === MentionBoostDestinationType.PRODUCT_PAGE) {
      return {
        destinationType,
        product: selectedProduct ? { shopifyProductId: selectedProduct.shopifyProductId } : null,
        targetUrl: customTargetUrlEnabled ? trimmedTargetUrl : null,
        budgetAmountInCents,
        durationDays,
        audienceMode,
        targetLocations,
        targetAgeRange: effectiveTargetAgeRange,
        utmParameters: cleanUtmParameters(utmParameters),
        ...placementPayload,
      };
    }

    // WEBSITE
    return {
      destinationType,
      targetUrl: trimmedWebsiteUrl,
      budgetAmountInCents,
      durationDays,
      audienceMode,
      targetLocations,
      targetAgeRange: effectiveTargetAgeRange,
      utmParameters: cleanUtmParameters(utmParameters),
      ...placementPayload,
    };
  }, [
    audienceMode,
    budgetAmountInCents,
    customTargetUrlEnabled,
    destinationType,
    durationDays,
    selectedProduct,
    effectiveTargetAgeRange,
    targetLocations,
    trimmedTargetUrl,
    trimmedWebsiteUrl,
    utmParameters,
    placementPayload,
  ]);

  const currentDraft = useMemo(() => serializeDraft(buildPayload()), [buildPayload]);
  const isDirty = currentDraft !== baselineRef.current;
  // Keep the mirror in sync for the hydrate effect (declared above, runs after
  // this render). Assigning a ref during render is idempotent and side-effect free.
  isDirtyRef.current = isDirty;

  const saveDraft = useCallback(async () => {
    if (!canSave) return;
    await saveSetupAsync({ mentionId, ...buildPayload() });
    // The just-saved draft becomes the new baseline, so it is not re-saved and
    // does not read as dirty until the next edit.
    baselineRef.current = currentDraft;
  }, [buildPayload, canSave, currentDraft, mentionId, saveSetupAsync]);

  // Debounced background auto-save: ~1s after the brand pauses on a dirty, valid
  // draft, persist it WHILE the panel is mounted (never on close/unmount, which
  // would race teardown and risk losing the edit). Failures surface through the
  // save mutation's onError toast; the rejection is caught here so it never leaks
  // as an unhandled promise. The payload is pre-cleaned (invalid/duplicate UTM
  // keys dropped) so a bad key never triggers a rejected-save retry loop.
  useEffect(() => {
    if (!isDirty || !canSave || isSaving || !isOpen) return;

    const timer = window.setTimeout(() => {
      void saveDraft().catch(() => {});
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [canSave, isDirty, isOpen, isSaving, saveDraft]);

  return {
    isLoading,
    isError,
    isSaving,
    setup: data?.setup ?? null,
    launchedBoost: data?.launchedBoost ?? null,
    eligibility: data?.eligibility ?? null,
    readiness: data?.readiness ?? null,
    isShopifyConnected,
    destinationType,
    setDestinationType,
    selectedProduct,
    selectedProductIds,
    handleProductToggle,
    selectedProductUrl,
    customTargetUrlEnabled,
    targetUrl,
    setTargetUrl,
    handleCustomTargetUrlChange,
    targetUrlIsValid,
    targetUrlIsInvalid,
    targetUrlPreview,
    websiteUrl,
    setWebsiteUrl,
    websiteUrlIsValid,
    websiteUrlIsInvalid,
    showUtmSection,
    isUtmConventionPristine,
    utmDisplayParameters,
    utmLockedKeys,
    utmDestinationUrl,
    utmPreviewUrl,
    utmUrlTags,
    utmAppliedCount,
    onUtmValueChange,
    onUtmKeyChange,
    onUtmAddParameter,
    onUtmRemoveParameter,
    onUtmDisableTracking,
    onUtmEnableTracking,
    placementMode,
    placementCampaignId,
    placementCampaignName,
    setPlacement,
    budgetAmountInCents,
    budgetCurrencyCode,
    budgetCurrencyDisplay,
    budgetWholeUnitInvalid,
    durationDays,
    handleBudgetChange,
    handleDurationChange,
    durationCollection,
    audienceMode,
    advantagePlusEnabled,
    handleAdvantagePlusChange,
    targetLocations,
    setTargetLocations,
    targetAgeRange,
    ageCollection,
    advantagePlusAgeCollection,
    advantagePlusMinimumAgeInvalid,
    handleTargetAgeMinChange,
    handleTargetAgeMaxChange,
    canSave,
    isDirty,
    saveDraft,
  };
}
